import { useQuery } from '@apollo/react-hooks';
import React, { useCallback } from 'react';
import type { MessageDescriptor } from 'react-intl-next';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl-next';

import type { DropdownItemProps } from '@atlaskit/dropdown-menu';
import { DropdownItem, DropdownItemGroup } from '@atlaskit/dropdown-menu';
import Icon from '@atlaskit/icon/base';
import GraphLineIcon from '@atlaskit/icon/core/migration/chart-trend--graph-line';
import FeedbackIcon from '@atlaskit/icon/core/migration/feedback';
import LockIcon from '@atlaskit/icon/core/migration/lock-locked--lock';
import AkBrushIcon from '@atlaskit/icon/glyph/media-services/brush';
import { token } from '@atlaskit/tokens';

import { fg } from '@confluence/feature-gating';
import {
	FeedbackType,
	useFeedbackCollector,
} from '@confluence/company-hub-utils/entry-points/FeedbackCollectorLoader';
import { useIsCompanyHubEnabled } from '@confluence/company-hub-utils/entry-points/useIsCompanyHubEnabled';
import { ChangeHubNameDialog } from '@confluence/company-hub/entry-points/ChangeHubNameDialog';
import { UnpublishHubDialogLoadable } from '@confluence/company-hub/entry-points/UnpublishHubDialogLoadable';
import { useIsProductAdmin } from '@confluence/current-user';
import type { DialogsStateContainer } from '@confluence/dialogs';
import {
	SPACE_PERMISSIONS_SETTINGS,
	COMPANY_HUB_ANALYTICS,
	ADMIN_CUSTOMIZE_NAVIGATION,
	ADMIN_COMPANY_HUB_PERMISSIONS,
} from '@confluence/named-routes';
import { isCompanyHubSpaceKey } from '@confluence/route-manager/entry-points/companyHubUtils';

import { RenameIcon } from './icons/RenameIcon';
import { UnpublishHubIcon } from './icons/UnpublishHubIcon';
import { SpaceAdminQuery } from './SpaceAdminQuery.graphql';
import type { SpaceAdminQuery as SpaceAdminQueryType } from './__types__/SpaceAdminQuery';
import type { EllipsisDropdownComponentProps } from './types';

const i18n = defineMessages({
	changeHubName: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.hub.change-name',
		description:
			'The label user can click to open up a modal to change the name of their company hub',
		defaultMessage: 'Change hub name',
	},
	companyHubAnalytics: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.hub.analytics',
		description: 'The label a user can click to open the hub analytics page',
		defaultMessage: 'Analytics',
	},
	companyHubSettingsTitle: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.company.hub.settings.title',
		description: 'Group title of dropdown items relevant to Company Hub Settings',
		defaultMessage: 'Company Hub Settings',
	},
	customizeNavigation: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.hub.customize-navigation',
		description: 'The label user can click to customize the navigation of their company hub',
		defaultMessage: 'Customize navigation',
	},
	hubPermissions: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.hub.permissions',
		description: 'The label user can click to navigate to Space Permissions Page',
		defaultMessage: 'Hub permissions',
	},
	shareFeedback: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.hub.share-feedback',
		description:
			'The dropdown menu item in the editor the user can click to open a modal to share their feedback on the feature Company Hub',
		defaultMessage: 'Share your thoughts',
	},
	unPublishHubItemLabel: {
		id: 'editor-ellipsis-dropdown.open.ellipsis.unpublish-hub',
		description: 'The label user can click to open up a modal to unpublish their company hub',
		defaultMessage: 'Deactivate hub',
	},
});

export function useCompanyHubSettingsGroupMenuItems({
	spaceKey,
	contentId,
}: Pick<EllipsisDropdownComponentProps, 'spaceKey' | 'contentId'>) {
	const intl = useIntl();
	const { isCompanyHubPublished } = useIsCompanyHubEnabled();
	const { isProductAdmin } = useIsProductAdmin();

	const isCompanyHub = isCompanyHubSpaceKey(spaceKey);
	const isHubDashboardEnabled = fg('company_hub_consolidated_settings'); // Hub Dashboard aka Consolidated Settings: https://hello.atlassian.net/wiki/spaces/CLI/pages/4111377280/Project+Poster+Hub+Dashboard

	const { data } = useQuery<SpaceAdminQueryType>(SpaceAdminQuery, {
		variables: {
			spaceKey,
		},
		skip:
			!isCompanyHub ||
			// Hub Dashboard only has the menu item 'Share your feedback' which is available to anyone who the editor is available to so there's no need to query any data:
			isHubDashboardEnabled,
	});
	const isSpaceAdmin = Boolean(data?.space?.currentUser?.isAdmin);

	const openFeedbackCollector = useFeedbackCollector({
		source: 'hubEditorEllipsisDropdown',
		type: FeedbackType.COMPANY_HUB,
	});

	return useCallback(
		(menuItems: JSX.Element[], dialogs: DialogsStateContainer) => {
			if (!isCompanyHub) {
				return;
			}

			if (isHubDashboardEnabled) {
				//
				// The new Hub Dashboard replaces the GA version of the Company Hub Settings group of menu items in the editor ellipsis dropdown:
				//

				menuItems.push(
					<DropdownItemGroup hasSeparator={menuItems.length >= 1} key="company-hub-settings-items">
						<DropdownItem
							key="share-feedback"
							elemBefore={<FeedbackIcon label="" />}
							onClick={openFeedbackCollector}
						>
							<FormattedMessage {...i18n.shareFeedback} />
						</DropdownItem>
					</DropdownItemGroup>,
				);

				return;
			}

			//
			// The GA version of the Company Hub Settings group of menu items in the editor ellipsis dropdown before their consolidation into the new Hub Dashboard:
			//

			const companyHubSettingsItems = [] as JSX.Element[];

			const addRedirectMenuItem = (
				label: MessageDescriptor,
				elemBefore: DropdownItemProps['elemBefore'],
				href: string,
			) => {
				companyHubSettingsItems.push(
					<DropdownItem
						key="redirect-admin"
						elemBefore={elemBefore}
						href={href}
						UNSAFE_shouldDisableRouterLink
					>
						<FormattedMessage {...label} />
					</DropdownItem>,
				);
			};

			if (isSpaceAdmin) {
				companyHubSettingsItems.push(
					<DropdownItem
						key="change-hub-name"
						elemBefore={
							<Icon label="" glyph={RenameIcon} primaryColor={token('color.icon.subtle')} />
						}
						onClick={() => {
							dialogs.showDialog(ChangeHubNameDialog);
						}}
					>
						<FormattedMessage {...i18n.changeHubName} />
					</DropdownItem>,
				);
			}

			if (fg('confluence_frontend_company_hub_analytics')) {
				addRedirectMenuItem(
					i18n.companyHubAnalytics,
					<GraphLineIcon label="" color={token('color.icon.subtle')} />,
					COMPANY_HUB_ANALYTICS.toUrl(),
				);
			}

			if (isSpaceAdmin) {
				addRedirectMenuItem(
					i18n.hubPermissions,
					<LockIcon label="" color={token('color.icon.subtle')} />,
					fg('confluence_frontend_company_hub_simplified_routes')
						? ADMIN_COMPANY_HUB_PERMISSIONS.toUrl()
						: SPACE_PERMISSIONS_SETTINGS.toUrl({ spaceKey }),
				);
			}

			if (isProductAdmin) {
				addRedirectMenuItem(
					i18n.customizeNavigation,
					<AkBrushIcon label="" primaryColor={token('color.icon.subtle')} />,
					ADMIN_CUSTOMIZE_NAVIGATION.toUrl(),
				);
			}

			if (isCompanyHubPublished && isProductAdmin) {
				companyHubSettingsItems.push(
					<DropdownItem
						key="unpublish-hub"
						elemBefore={<UnpublishHubIcon />}
						onClick={() => {
							dialogs.showModal(UnpublishHubDialogLoadable, { contentId });
						}}
					>
						<FormattedMessage {...i18n.unPublishHubItemLabel} />
					</DropdownItem>,
				);
			}

			companyHubSettingsItems.length &&
				menuItems.push(
					<DropdownItemGroup
						hasSeparator={menuItems.length >= 1}
						key="company-hub-settings-items"
						title={intl.formatMessage(i18n.companyHubSettingsTitle)}
					>
						{companyHubSettingsItems}
					</DropdownItemGroup>,
				);
		},
		[
			intl,
			isCompanyHub,
			isCompanyHubPublished,
			isHubDashboardEnabled,
			isProductAdmin,
			isSpaceAdmin,
			openFeedbackCollector,
			spaceKey,
			contentId,
		],
	);
}
